import {Button, Col, Form, Input, Modal, Row, Select, Spin, Table, Collapse, Descriptions, Typography} from "antd";
import React, {useContext, useEffect, useRef, useState} from "react";
import {AuditLogEventWithUser} from "../api-client";
import {getAuditApi} from "../api/apiInstances";
import {CaretDownOutlined, InfoCircleOutlined, SearchOutlined} from "@ant-design/icons";
import {FormattedMessage, useIntl} from "react-intl";
import '../assets/css/ActivityLog.css';
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import DetailWrapper from "../components/common/DetailWrapper";
import {accessibilityRoles} from "../utils/accessUtils";
import AccessControl from "../components/common/AccessControl";
import {messages} from "../i18n/activity-log";
import DetailsModalContent from "../components/activityLog/DetailsModalContent";
import {DentobookApplicationContext} from "../context/DentobookApplicationContext";
import {Navigate} from "react-router-dom";
import logger from "../utils/logger";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text } = Typography;

const ActivityLog = () => {

    const {principal} = useContext(DentobookApplicationContext);

    const intl = useIntl();
    const [keyword, setKeyword] = useState<string>('');
    const [filterBy, setFilterBy] = useState<string>('');
    const sortBy: 'id' | 'date' = 'date';
    const [order, setOrderBy] = useState<'asc' | 'desc'>('desc');
    const [auditlogs, setAuditLogs] = useState<AuditLogEventWithUser[]>([]);
    const [totalElements, setTotalElements] = useState<number>(0);
    const pageSize: number = 50;
    const auditLogApi = getAuditApi();
    const [currentPage, setCurrentPage] = useState<number>(1);  // Ant Design uses 1-based pagination
    const [loading, setLoading] = useState<boolean>(false);
    const isMounted = useRef(true);
    const filterKeysContainingId = (obj: Record<string, any>): Record<string, any> => {
        return Object.keys(obj).reduce((acc: Record<string, any>, key) => {
            if (!key.toLowerCase().includes('id')) {
                // Check if the value is an object and recursively filter it if so
                if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
                    acc[key] = filterKeysContainingId(obj[key]);
                } else {
                    acc[key] = obj[key];
                }
            }
            return acc;
        }, {});
    };
    const formatLogDetails = (log: AuditLogEventWithUser) => {
        const translateAndFilter = (data: Record<string, any>) => {
            // Filter out keys containing 'id' first
            let filteredData = filterKeysContainingId(data);

            // Then translate the filtered data (dates are formatted within translateFields)
            return translateFields(filteredData);
        };

        const formattedBefore = translateAndFilter(
            JSON.parse(log.affectedEntityStateBeforeChange ?? '{}')
        );
        const formattedAfter = translateAndFilter(
            JSON.parse(log.affectedEntityStateAfterChange ?? '{}')
        );

        return { formattedBefore, formattedAfter };
    };
    interface FieldsTranslations {
        [key: string]: string;
    }

    const translateFields = (log: Record<string, any>): Record<string, any> => {
        const translations: FieldsTranslations = messages[intl.locale].fields;

        const formatDate = (dateString: string | number | Date | null) => {
            if (dateString === null) {
                return null;
            }
            return formatDateString(dateString);
        };

        // Convert snake_case to camelCase for field name translation lookup
        const snakeToCamel = (str: string): string => {
            return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
        };

        const translateWord = (word: string): string => {
            // Try direct match first
            if (translations.hasOwnProperty(word)) {
                return translations[word];
            }
            // Try camelCase version (for snake_case field names from backend)
            const camelCase = snakeToCamel(word);
            if (translations.hasOwnProperty(camelCase)) {
                return translations[camelCase];
            }
            // Return original if no translation found
            return word;
        };

        const translateValue = (value: any): any => {
            if (value === null || value === undefined) {
                // Keep null/undefined as-is, will be styled in rendering
                return null;
            }
            if (typeof value === 'string') {
                // Updated regex to handle timezone offsets like +00:00 or -05:00
                const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;
                if (dateRegex.test(value)) {
                    return formatDate(value);
                }
                // Check if value is an enum (UPPERCASE_WITH_UNDERSCORES)
                const enumPattern = /^[A-Z][A-Z_]*[A-Z]$/;
                if (enumPattern.test(value)) {
                    // Try to translate using main messages (like PatientDetailsView does)
                    try {
                        return intl.formatMessage({ id: value.toLowerCase() });
                    } catch (e) {
                        // If translation doesn't exist, return original
                        return value;
                    }
                }
                return value;
            } else if (typeof value === 'boolean') {
                return translateWord(value.toString());
            } else if (typeof value === 'object' && !Array.isArray(value)) {
                // Recursively translate nested objects
                return Object.entries(value).reduce((acc: Record<string, any>, [key, val]) => {
                    acc[translateWord(key)] = translateValue(val);
                    return acc;
                }, {});
            }
            return value;
        };

        const translateObject = (obj: Record<string, any>): Record<string, any> => {
            return Object.keys(obj).reduce<Record<string, any>>((newObj, key) => {
                // Translate the key
                const translatedKey = translateWord(key);

                // Filter out keys containing 'id' after translation
                if (!translatedKey.toLowerCase().includes('id')) {
                    newObj[translatedKey] = translateValue(obj[key]);
                }
                return newObj;
            }, {});
        };

        return translateObject(log);
    };

    const renderDescriptionItem = (label: string, value: any, level = 0) => {
        if (/id/i.test(label)) return null;

        let displayValue: React.JSX.Element | string;

        if (typeof value === 'object' && value !== null) {
            if (Object.keys(value).length > 0 && level === 0) {
                displayValue = (
                    <Descriptions bordered column={1}>
                        {Object.entries(value).map(([nestedKey, nestedValue]) =>
                            renderDescriptionItem(nestedKey, nestedValue, level + 1)
                        )}
                    </Descriptions>
                );
            } else {
                const formattedNestedItems = formatNestedObject(value);

                displayValue = (
                    <Collapse bordered={false}>
                        <Collapse.Panel header={label} key={label}>
                            {formattedNestedItems}
                        </Collapse.Panel>
                    </Collapse>
                );
            }
        } else {
            // Check if value is null/undefined per API contract and render styled "No data"
            if (value === null || value === undefined) {
                displayValue = <Text type="secondary">{intl.formatMessage({ id: 'no_data' })}</Text>;
            } else {
                displayValue = value.toString();
            }
        }

        return (
            <Descriptions.Item key={label} label={label}>
                {displayValue}
            </Descriptions.Item>
        );
    };


    function getFormattedValue(val: { toString: () => any; } | null | undefined) {
        if (val !== null && val !== undefined) {
            return val.toString();
        } else {
            return <Text type="secondary">{intl.formatMessage({ id: 'no_data' })}</Text>;
        }
    }

    function formatNestedObject(obj: object) {
        return Object.entries(obj).map(([key, val]) => {
            const formattedValue = typeof val === 'object' && val !== null
                ? formatNestedObject(val) // Recursively format nested objects
                : getFormattedValue(val);
            return <div key={key}><strong>{key}:</strong> {formattedValue}</div>;
        });
    }

    const renderLogDetails = (logDetails: Record<string, any>) => {
        return (
            <Descriptions bordered column={1}>
                {Object.entries(logDetails).map(([key, value]) =>
                    renderDescriptionItem(key, value)
                )}
            </Descriptions>
        );
    };

    const renderDate = (log: AuditLogEventWithUser) => (
        <div>
        <span className="font-weight-500">
            {log.updatedAt
                ? dayjs.utc(log.updatedAt).local().format('DD.MM.YYYY HH:mm:ss')
                : '-'}
        </span>
        </div>
    );


    const renderUser = (log: AuditLogEventWithUser) => (
        <span className="font-weight-500">
        {log.userFirstName ?? '-'} {log.userLastName ?? '-'}
    </span>
    );


    const getActionKey = (log: AuditLogEventWithUser): string => {
        // When affectedEntityType is null (e.g., LOGIN events), use only event type
        if (!log.affectedEntityType) {
            return `action_${log.eventType}`;
        }
        // Otherwise, combine event type and affected entity type
        return `action_${log.eventType}_${log.affectedEntityType}`;
    };

    const renderAction = (log: AuditLogEventWithUser) => {
        const actionKey = getActionKey(log);
        return <span className="font-weight-500"><FormattedMessage id={actionKey} /></span>;
    };

    const renderDetailsButton = (log: AuditLogEventWithUser) => {
        // Only show details icon if there's an affected entity (CRUD operation)
        if (!log.affectedEntityType) {
            return null;
        }
        return (
            <Button type="link" onClick={() => showDetailsModal(log)}>
                <InfoCircleOutlined className="dentobook-link" style={{ fontSize: '20px' }} />
            </Button>
        );
    };


    const auditlogsWithKeys = auditlogs.map((log, index) => ({
        ...log,
        key: (log.id ?? log.updatedAt) ?? `unique-${index}`
    }));


    const showDetailsModal = (log: AuditLogEventWithUser) => {
        Modal.info({
            closable: true,
            icon: null,
            content: (
                <DetailsModalContent
                    log={log}
                    renderLogDetails={renderLogDetails}
                    formatLogDetails={formatLogDetails}
                    intl={intl}
                />
            ),
            className: 'custom-details-modal',
            afterClose: () => {
                document.body.style.overflow = '';
                document.body.style.paddingRight = '';
            },
            footer: [
                <div key="footer-wrapper" className="footer-wrapper">
                    <Button
                        key="close"
                        type="primary"
                        onClick={() => Modal.destroyAll()}
                    >
                        {intl.formatMessage({ id: 'close' })}
                    </Button>
                </div>,
            ],
        });

        setTimeout(() => {
            const modalContent = document.querySelector('.custom-details-modal .ant-modal-body');
            if (modalContent) modalContent.scrollTop = 0;
            document.body.style.overflow = 'hidden';
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.paddingRight = `${scrollbarWidth}px`;
        }, 100);
    };

    const fetchData = async (page: number = currentPage) => {
        if (loading) return;

        setLoading(true);

        try {
            // API uses 0-based pagination, Ant Design uses 1-based
            const pageToFetch = page - 1;
            const response = await auditLogApi.fetchAuditLogEvents(
                keyword,
                filterBy as "create" | "update" | "delete" | "user" | undefined,
                undefined,  // entityType
                pageToFetch,
                pageSize,
                sortBy,
                order
            );

            if (response.data && isMounted.current) {
                const { totalElements, content: auditLogEvents } = response.data;
                if (auditLogEvents) {
                    setAuditLogs(auditLogEvents);
                }
                if (totalElements !== undefined) setTotalElements(totalElements);
            }
        } catch (err) {
            logger.error(err);
        } finally {
            setLoading(false);
        }
    }

    const handleTableChange = (pagination: any) => {
        const newPage = pagination.current;
        setCurrentPage(newPage);
        fetchData(newPage);
    };

    useEffect(() => {
        isMounted.current = true;
        setCurrentPage(1);  // Reset to page 1 when filters change
        fetchData(1);

        return () => {
            isMounted.current = false;
        };
    }, [keyword, filterBy, sortBy, order]);

    if (!principal?.features.includes('ACTIVITY_LOG')) {
        return <Navigate to={'/'}/>;
    }

    return (
        <AccessControl
            accessibleFor={[accessibilityRoles.CLINIC_ADMIN]}>
            <DetailWrapper navigateBack={undefined}>
                <Row className="mb-2">
                    <Col span={12}>
                        <h3><FormattedMessage id="activity_log"/></h3>
                    </Col>
                </Row>
                <Form layout="horizontal">
                    <Row gutter={16}>
                        <Col flex={5}>
                            <Form.Item
                                name="keyword"
                                className={"dentobook-form-item"}
                            >
                                <Input value={keyword}
                                       placeholder={intl.formatMessage({id: 'search'})}
                                       onChange={e => setKeyword(e.target.value)}
                                       prefix={<SearchOutlined style={{fontSize: 24}}/>}
                                />
                            </Form.Item>
                        </Col>
                        <Col flex={0}>
                            <Form.Item name="filterBy"
                                       className={"dentobook-form-item sort-by-input"}>
                                <Select
                                    key={filterBy}
                                    size={"large"}
                                    allowClear
                                    value={filterBy}
                                    suffixIcon={<CaretDownOutlined/>}
                                    onChange={e => setFilterBy(e)}
                                    placeholder={intl.formatMessage({id: 'filterBy'})}>
                                    <Select.Option key="create" value="create"><FormattedMessage
                                        id="create"/></Select.Option>
                                    <Select.Option key="update" value="update"><FormattedMessage
                                        id="update"/></Select.Option>
                                    <Select.Option key="delete" value="delete"><FormattedMessage
                                        id="delete"/></Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col flex={0}>
                            <Form.Item name="sortBy"
                                       className={"dentobook-form-item sort-by-input"}
                            >
                                <Select
                                    size={"large"}
                                    allowClear
                                    value={order}
                                    suffixIcon={<CaretDownOutlined/>}
                                    onChange={e => setOrderBy(e)}
                                    placeholder={intl.formatMessage({id: 'sortBy'})}
                                >
                                    <Select.Option value="asc"><FormattedMessage id="date"/><FormattedMessage id="asc"/></Select.Option>
                                    <Select.Option value="desc"><FormattedMessage id="date"/><FormattedMessage
                                        id="desc"/></Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Button type="link" htmlType="submit" onClick={() => fetchData(1)}></Button>
                </Form>
                <Table
                    dataSource={auditlogsWithKeys}
                    rowKey="key"
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalElements,
                        showSizeChanger: false,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        position: ['bottomCenter']
                    }}
                    onChange={handleTableChange}
                    className="dentobook-table"
                    scroll={{ x: true }}
                >
                        <Table.Column
                            key="date"
                            title={<FormattedMessage id="date" />}
                            render={renderDate}
                        />
                        <Table.Column
                            key="user"
                            title={<FormattedMessage id="user" />}
                            render={renderUser}
                        />
                        <Table.Column
                            key="action"
                            title={<FormattedMessage id="action" />}
                            render={renderAction}
                        />
                        <Table.Column
                            key="details"
                            title={<FormattedMessage id="details" />}
                            render={renderDetailsButton}
                        />
                </Table>
            </DetailWrapper>
        </AccessControl>
    )
};

function formatIsoString(isoString: string | number | Date) {
    if (!isoString) {
        return '';
    }
    return formatDateString(isoString);
}

function formatDateString(dateString: string | number | Date) {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }
    return dateString;
}

export default ActivityLog;